/**
 * Wire Phase 1 notification events from desk worker rows into the player.
 *
 * Kept pure where possible so tests can drive detection without React.
 */

import {
  detectNotificationEvents,
  type NotificationSoundEvent,
  type NotificationWorkerSnapshot,
} from './notification-events';
import type { NotificationPlayer } from './notification-playback';

export interface WireWorkerRow {
  readonly id: string;
  readonly number: number;
  readonly status: string;
}

/**
 * Build snapshots from workers + pending-approval map + optional terminal
 * permission flags (waiting_for_approval / waiting_for_input).
 */
export function buildNotificationSnapshots(
  workers: readonly WireWorkerRow[],
  pendingByWorker: ReadonlyMap<string, number>,
  terminalNeedsPermission: ReadonlyMap<string, boolean> = new Map(),
): readonly NotificationWorkerSnapshot[] {
  return workers.map((worker) => ({
    workerId: worker.id,
    workerNumber: worker.number,
    status: worker.status,
    needsPermission:
      (pendingByWorker.get(worker.id) ?? 0) > 0
      || terminalNeedsPermission.get(worker.id) === true,
  }));
}

/**
 * Diff snapshots and enqueue newly detected events. Returns enqueued count.
 */
export async function dispatchNotificationTransitions(
  player: NotificationPlayer,
  previous: readonly NotificationWorkerSnapshot[],
  next: readonly NotificationWorkerSnapshot[],
): Promise<{ readonly events: readonly NotificationSoundEvent[]; readonly enqueued: number }> {
  const events = detectNotificationEvents(previous, next);
  let enqueued = 0;
  for (const event of events) {
    const ok = await player.enqueueEvent(event);
    if (ok) enqueued += 1;
  }
  return { events, enqueued };
}
